import React from "react";
import { Accordion, Badge } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPalette, faCode } from "@fortawesome/free-solid-svg-icons";
import ReactJson from "react-json-view";
import { useBrandingProfile } from "../../../../hooks/useBrandingProfile";

function pickImage(imageObj, preferredSizes) {
    if (!imageObj) return null;
    for (const size of preferredSizes) {
        if (imageObj[size]) return imageObj[size];
    }
    return imageObj.original || null;
}

const sectionStyle = {
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '12px 14px',
    marginBottom: '10px',
};

const labelStyle = {
    fontSize: '0.68rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: '#888',
    display: 'block',
    marginBottom: '3px',
};

const valueStyle = {
    fontSize: '0.9rem',
    fontWeight: '600',
    color: '#222',
};

function Field({ label, children }) {
    return (
        <div style={{ marginBottom: '6px' }}>
            <span style={labelStyle}>{label}</span>
            <div style={valueStyle}>{children}</div>
        </div>
    );
}

function BrandingProfileCard({ bondId }) {
    const profile = useBrandingProfile(bondId);

    if (!profile) return null;

    const bannerUrl = pickImage(profile.images?.banner, ['540x228', '360x152']);
    const logoUrl = profile.brand?.images?.logo?.['144x144']
        || pickImage(profile.brand?.images?.logo, ['96x96', '72x72', '48x48']);
    const logoWideUrl = pickImage(profile.brand?.images?.logo_wide, ['204x72', '136x48']);
    const lifestyleUrl = pickImage(profile.brand?.images?.lifestyle_branded, ['375x304', '562x456']);
    const brand = profile.brand || {};
    const featuredTechs = profile.featured_technologies || [];
    const deviceTypeProfiles = Object.values(profile.bridge_device_type_profiles || {});

    return (
        <Accordion className="mb-2">
            <Accordion.Item eventKey="branding">
                <Accordion.Header>
                    <div className="d-flex align-items-center w-100 me-2">
                        <FontAwesomeIcon icon={faPalette} className="me-2" />
                        Customization Profile
                        {logoUrl && (
                            <img
                                src={logoUrl}
                                alt={brand.display_name}
                                className="ms-auto"
                                style={{ width: '28px', height: '28px', objectFit: 'contain', borderRadius: '4px' }}
                            />
                        )}
                    </div>
                </Accordion.Header>
                <Accordion.Body style={{ backgroundColor: '#e8e8e8', padding: '12px' }}>

                    {/* Profile name — top */}
                    <div style={sectionStyle}>
                        <Field label="Profile Name">{profile.name}</Field>
                    </div>

                    {/* Brand */}
                    {brand.display_name && (
                        <div style={sectionStyle}>
                            <span style={{ ...labelStyle, marginBottom: '10px' }}>Brand</span>
                            <div className="d-flex align-items-start gap-3">
                                {/* Logo */}
                                {logoUrl && (
                                    <div style={{ flexShrink: 0 }}>
                                        <span style={labelStyle}>Logo</span>
                                        <div style={{
                                            width: '64px', height: '64px',
                                            backgroundColor: 'white',
                                            borderRadius: '8px',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            border: '1px solid #e0e0e0',
                                        }}>
                                            <img
                                                src={logoUrl}
                                                alt={brand.display_name}
                                                style={{ width: '52px', height: '52px', objectFit: 'contain' }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Display name + URL + wide logo in one row */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className="d-flex align-items-start justify-content-between gap-2">
                                        {/* Left: name + URL */}
                                        <div>
                                            <Field label="Display Name">
                                                {brand.url ? (
                                                    <a href={brand.url} target="_blank" rel="noopener noreferrer"
                                                       style={{ color: '#222', textDecoration: 'none', fontWeight: 600 }}>
                                                        {brand.display_name}
                                                    </a>
                                                ) : brand.display_name}
                                            </Field>
                                            {brand.url && (
                                                <Field label="URL">
                                                    <span style={{ fontSize: '0.78rem', color: '#555', fontWeight: 400 }}>{brand.url}</span>
                                                </Field>
                                            )}
                                        </div>

                                        {/* Right: wide logo */}
                                        {logoWideUrl && (
                                            <div style={{ flexShrink: 0 }}>
                                                <span style={labelStyle}>Wide Logo</span>
                                                <div style={{
                                                    backgroundColor: '#f5f5f5',
                                                    display: 'inline-flex',
                                                    padding: '6px 10px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #e0e0e0',
                                                }}>
                                                    <img
                                                        src={logoWideUrl}
                                                        alt={`${brand.display_name} wide`}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Lifestyle image */}
                                    {lifestyleUrl && (
                                        <div style={{ marginTop: '10px' }}>
                                            <span style={labelStyle}>Lifestyle Image</span>
                                            <img
                                                src={lifestyleUrl}
                                                alt="Lifestyle"
                                                style={{ borderRadius: '6px' }}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Banner */}
                    {bannerUrl && (
                        <div style={sectionStyle}>
                            <span style={labelStyle}>Banner</span>
                            <div style={{ textAlign: 'center' }}>
                                <img
                                    src={bannerUrl}
                                    alt="Profile banner"
                                    style={{ objectFit: 'cover', borderRadius: '6px' }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Featured Technologies */}
                    {featuredTechs.length > 0 && (
                        <div style={sectionStyle}>
                            <span style={labelStyle}>Featured Technologies</span>
                            <div className="d-flex flex-column gap-2 mt-1">
                                {featuredTechs.map((tech, idx) => (
                                    <div key={idx} className="d-flex align-items-center gap-2">
                                        <Badge bg="secondary" style={{ fontSize: '0.7rem' }}>{tech.device_type}</Badge>
                                        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#333' }}>{tech.name}</span>
                                        {tech.description && (
                                            <span style={{ fontSize: '0.8rem', color: '#777' }}>— {tech.description}</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Device Type Profiles */}
                    {deviceTypeProfiles.length > 0 && (
                        <div style={sectionStyle}>
                            <span style={labelStyle}>Device Type Profiles</span>
                            <div className="d-flex flex-column gap-2 mt-1">
                                {deviceTypeProfiles.map((dtProfile, idx) => {
                                    const dtBanner = dtProfile.images?.banner?.['360x152']
                                        || pickImage(dtProfile.images?.banner, ['540x228']);
                                    return (
                                        <div key={idx}>
                                            {dtBanner && (
                                                <div style={{ textAlign: 'center', marginBottom: '6px' }}>
                                                    <img
                                                        src={dtBanner}
                                                        alt={dtProfile.name}
                                                        style={{ borderRadius: '6px' }}
                                                    />
                                                </div>
                                            )}
                                            <div className="d-flex align-items-center gap-2">
                                                <Badge bg="info" style={{ fontSize: '0.7rem' }}>{dtProfile.device_type}</Badge>
                                                <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#333' }}>{dtProfile.name}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Raw JSON */}
                    <Accordion>
                        <Accordion.Item eventKey="json">
                            <Accordion.Header>
                                <FontAwesomeIcon icon={faCode} className="me-2" />
                                Raw JSON
                            </Accordion.Header>
                            <Accordion.Body>
                                <ReactJson
                                    src={profile}
                                    theme="ocean"
                                    collapsed={1}
                                    displayDataTypes={false}
                                    displayObjectSize={false}
                                    enableClipboard={true}
                                    name={false}
                                />
                            </Accordion.Body>
                        </Accordion.Item>
                    </Accordion>

                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
}

export default BrandingProfileCard;
